'use client'

import { type MouseEvent, type ReactNode } from 'react'

import { EmptyState } from '@/app/components/shared/EmptyState'
import { InfiniteVirtualizedList } from '@/app/components/shared/InfiniteVirtualizedList'
import { TrackedProductCard } from '@/app/components/shared/TrackedProductCard'
import { HorizontalCard } from '@/app/components/shared/cards/HorizontalCard'
import { VerticalCard } from '@/app/components/shared/cards/VerticalCard'
import { useOptionalPageAnalytics } from '@/app/context/analytics'
import { useViewModeContext } from '@/app/context/viewMode'
import { FILTERED_LISTING_MODULE_TYPE } from '@/app/lib/analytics/constants'
import { mapProductToAnalyticsItem } from '@/app/lib/analytics/mapProductToAnalyticsItem'
import { useProductListTracking } from '@/app/lib/analytics/useProductListTracking'
import type { LoMasVendidoQueryFilters } from '@/app/lib/api/api'
import { isLinkableProduct, mapProductToCard } from '@/app/lib/api/mapProductToCard'
import type { ProductApiItem } from '@/app/lib/api/types-api'
import {
  COLUMN_GAP,
  COLUMN_GAP_PX,
  PAGE_PARAM,
  ROW_GAP_PX,
  SCROLL_THRESHOLD,
} from '@/app/lib/constants'
import { useIsDesktopViewport } from '@/app/lib/hooks/useIsDesktopViewport'
import type { InfiniteListPageLoadedInfo } from '@/app/lib/hooks/useInfiniteList'
import { computeHorizontalColumns, useResponsiveColumns } from '@/app/lib/hooks/useResponsiveColumns'
import { estimateHorizontalRowHeight, estimateRowHeight } from '@/app/lib/utils'

import { fetchLoMasVendidoPageAction } from '@/app/entradas/(general-web)/lo-mas-vendido/[location]/_components/actions'
import {
  ANALYTICS_ITEM_LIST_ID,
  ANALYTICS_ITEM_LIST_NAME,
  ANALYTICS_MODULE_NAME,
  ANALYTICS_MODULE_POSITION,
} from '@/app/entradas/(general-web)/lo-mas-vendido/[location]/_components/constants'

interface LoMasVendidoResultsProps {
  /** Productos de la página inicial, ya obtenidos server-side. */
  readonly initialProducts: ProductApiItem[]
  /** Página con la que arrancó la navegación (1, o la de `?page=N`). */
  readonly initialPage: number
  readonly totalPages: number
  /** `per_page` real de `/data` para esta consulta (FR-016: sin default compartido). */
  readonly pageSize: number
  readonly location: string
  readonly filters: LoMasVendidoQueryFilters
  /**
   * `SeoPaginationLinks` (Server Component) ya resuelto por `page.tsx` — ver
   * `InfiniteVirtualizedList`. Obligatorio a propósito: que este componente lo
   * reciba como prop, en vez de que `page.tsx` lo renderizara como un hermano
   * suelto, es justo lo que evita que una futura página con este mismo patrón
   * se olvide de incluirlo.
   */
  readonly paginationLinks: ReactNode
}

/**
 * `LoMasVendidoResults` — adaptador fino de `InfiniteVirtualizedList`
 * (`@/app/components/shared/InfiniteVirtualizedList`: scroll infinito +
 * virtualización + paginación SEO, genérico) con los datos concretos de esta
 * página: `VerticalCard`/`mapProductToCard` para el render de cada evento y
 * `fetchLoMasVendidoPageAction` como adaptador de datos. No gestiona su propio
 * reinicio de estado: `page.tsx` la desmonta/remonta con una `key` cuando
 * cambian `location`/filtros/`page` (ver data-model.md §Relaciones y flujo).
 *
 * La vista horizontal/vertical la controla `ViewModeContext` (compartido con
 * el `VistaToggle` de `LoMasVendidoFilters`, junto al botón "Ordenar y
 * filtrar") — ver specs/019-estados-compra-eventos. Requiere
 * `<ViewModeProvider>` como ancestro. El selector no está disponible en
 * mobile/tablet (oculto por `ListingFilters`), así que `isDesktop` fuerza
 * `VerticalCard` ahí con independencia del `viewMode` que hubiera quedado en
 * el contexto (p. ej. tras elegir horizontal en desktop y reducir la ventana).
 */
export function LoMasVendidoResults({
  initialProducts,
  initialPage,
  totalPages,
  pageSize,
  location,
  filters,
  paginationLinks,
}: LoMasVendidoResultsProps) {
  const { viewMode } = useViewModeContext()
  const isDesktop = useIsDesktopViewport()
  const isHorizontal = viewMode === 'horizontal' && isDesktop
  const analytics = useOptionalPageAnalytics()

  const { columns, estimatedRowHeight } = useResponsiveColumns({
    columnGapPx: COLUMN_GAP_PX,
    estimateRowHeight: isHorizontal ? estimateHorizontalRowHeight : estimateRowHeight,
    columnsOverride: isHorizontal ? computeHorizontalColumns : undefined,
  })

  // ── Analítica de e-commerce ────────────────────────────────────────────────
  // Impresiones agrupadas y deduplicadas (`view_item_list`) + `select_item`. La
  // dedup sobrevive al desmontaje de filas de la virtualización porque vive en
  // el hook, no en la card; se reinicia solo cuando `page.tsx` remonta este
  // componente (`key={resultsKey}`), justo cuando cambian location/filtros.
  const { trackImpression, trackSelect } = useProductListTracking(
    {
      itemListId: ANALYTICS_ITEM_LIST_ID,
      itemListName: ANALYTICS_ITEM_LIST_NAME,
      moduleName: ANALYTICS_MODULE_NAME,
      moduleType: FILTERED_LISTING_MODULE_TYPE,
      modulePosition: ANALYTICS_MODULE_POSITION,
    },
    { trackViewItemList: analytics?.trackViewItemList, trackSelectItem: analytics?.trackSelectItem },
  )

  function handlePageLoaded({ itemsLoadedTotal }: InfiniteListPageLoadedInfo) {
    // No hay CTA "Ver más": la carga es automática al llegar al sentinela. De
    // los dos eventos del diccionario, `view_more` es el que encaja ("cargar
    // más ítems sin cambiar de página"); `pagination` queda para los clicks
    // reales en los enlaces de `SeoPaginationLinks` (ver `handlePaginationClick`).
    analytics?.trackViewMore({
      module_name: ANALYTICS_MODULE_NAME,
      module_type: FILTERED_LISTING_MODULE_TYPE,
      module_position: ANALYTICS_MODULE_POSITION,
      items_loaded_total: String(itemsLoadedTotal),
    })
  }

  function handlePaginationClick(event: MouseEvent<HTMLDivElement>) {
    const { target } = event
    if (!(target instanceof Element)) return
    const link = target.closest('a[href]')
    if (!link) return
    const rel = link.getAttribute('rel')
    const href = link.getAttribute('href') ?? ''
    const page = new URLSearchParams(href.split('?')[1] ?? '').get(PAGE_PARAM) ?? '1'
    analytics?.trackPagination({
      pagination_method: rel === 'prev' ? 'prev' : rel === 'next' ? 'next' : 'direct_number',
      page_list_load: page,
      items_per_load: String(pageSize),
      module_name: ANALYTICS_MODULE_NAME,
      module_type: FILTERED_LISTING_MODULE_TYPE,
    })
  }

  // Envoltorio solo para delegar el click: `SeoPaginationLinks` es un Server
  // Component ya resuelto (llega como prop desde `page.tsx`), así que no admite
  // `onClick` propio. Está oculto visualmente (`visually-hidden`), de modo que
  // este `pagination` casi solo lo emitirán crawlers y lectores de pantalla.
  const trackedPaginationLinks = <div onClickCapture={handlePaginationClick}>{paginationLinks}</div>

  // `isLinkableProduct` descarta productos sin ficha (PDP) derivable — p. ej.
  // llegan sin `genres` — para no pintar una card sin destino ni en la carga
  // inicial ni en las páginas siguientes del scroll infinito (mismo criterio
  // que `toProductCards`/`toGridCards`, `mapProductToCard.ts`). `totalPages`
  // no se recalcula: viene tal cual del backend, así que filtrar aquí no
  // desincroniza la paginación de `InfiniteVirtualizedList` (solo puede
  // resultar en menos de `pageSize` cards visibles en alguna página).
  return (
    <InfiniteVirtualizedList<ProductApiItem>
      initialItems={initialProducts.filter(isLinkableProduct)}
      initialPage={initialPage}
      totalPages={totalPages}
      pageSize={pageSize}
      threshold={SCROLL_THRESHOLD}
      getItemKey={(item) => item.id}
      fetchPage={async (page) => {
        const result = await fetchLoMasVendidoPageAction(location, filters, page)
        return { ...result, items: result.items.filter(isLinkableProduct) }
      }}
      onPageLoaded={handlePageLoaded}
      renderItem={(item, index) => (
        <TrackedProductCard
          index={index}
          onImpression={() => trackImpression(item.id, mapProductToAnalyticsItem(item, index))}
          onSelect={() => trackSelect(mapProductToAnalyticsItem(item, index))}
        >
          {isHorizontal ? (
            <HorizontalCard {...mapProductToCard(item)} />
          ) : (
            <VerticalCard {...mapProductToCard(item)} />
          )}
        </TrackedProductCard>
      )}
      estimatedRowHeight={estimatedRowHeight}
      columns={columns}
      columnGap={COLUMN_GAP}
      rowGap={ROW_GAP_PX}
      pageParam={PAGE_PARAM}
      paginationLinks={trackedPaginationLinks}
      errorMessage="No se han podido cargar más eventos."
      loadingLabel="Cargando más eventos"
      emptyState={
        <EmptyState title="No hay eventos disponibles" description="Prueba a cambiar de provincia o de filtros." />
      }
    />
  )
}
